'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';

interface RequestActionsProps {
  requestId: string;
  portalSlug: string;
  initialTitle: string;
  initialDescription: string | null;
}

export function RequestActions({
  requestId,
  portalSlug,
  initialTitle,
  initialDescription,
}: RequestActionsProps) {
  const router = useRouter();
  const [mode, setMode] = useState<'idle' | 'edit' | 'confirmDelete'>('idle');
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(
        `/api/portal/${portalSlug}/requests/${requestId}/edit`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: title.trim(), description: description.trim() }),
        }
      );

      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        setError(data.error || 'Failed to update');
        return;
      }

      setMode('idle');
      router.refresh();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(
        `/api/portal/${portalSlug}/requests/${requestId}`,
        { method: 'DELETE' }
      );

      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        setError(data.error || 'Failed to delete');
        setIsSubmitting(false);
        return;
      }

      router.push(`/p/${portalSlug}`);
    } catch {
      setError('Network error. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (mode === 'edit') {
    return (
      <div className="border rounded-lg p-6 bg-card">
        <h3 className="text-sm font-semibold mb-3">Edit Request</h3>
        <form onSubmit={handleEdit} className="space-y-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            required
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isSubmitting || !title.trim()}
              className="bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('idle');
                setTitle(initialTitle);
                setDescription(initialDescription || '');
              }}
              className="px-3 py-1.5 rounded-md text-sm border hover:bg-accent"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (mode === 'confirmDelete') {
    return (
      <div className="border border-red-200 rounded-lg p-4 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
        <p className="text-sm font-medium mb-3">Delete this request? This cannot be undone.</p>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <div className="flex gap-2">
          <button
            onClick={handleDelete}
            disabled={isSubmitting}
            className="bg-red-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Deleting...' : 'Yes, Delete'}
          </button>
          <button
            onClick={() => setMode('idle')}
            className="px-3 py-1.5 rounded-md text-sm border hover:bg-accent"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <button
        onClick={() => setMode('edit')}
        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        Edit
      </button>
      <span className="text-muted-foreground">|</span>
      <button
        onClick={() => setMode('confirmDelete')}
        className="text-sm text-muted-foreground hover:text-red-600 transition-colors"
      >
        Delete
      </button>
    </div>
  );
}
